import { Coach, VideoUpload } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { personName } from '@backrs_ui/grow-ui/src/utils/personName';

import { Box, Grid, SxProps, Theme, Typography } from '@mui/material';

import { CoachEducationExperienceSection } from '../atoms/CoachEducationExperienceSection';

const COACH_PROFILE_STRINGS = appStrings.modules.profile.public.coachProfile;

type CoachProfileSnapshotProps = Readonly<{
  coaching: Coach;
  purposeVideoUrl?: string;
}>;

export const CoachProfileSnapshot = ({ coaching, purposeVideoUrl }: CoachProfileSnapshotProps) => {
  const hasVideo = !!purposeVideoUrl;
  const hasBio = !!coaching.bio?.trim();

  return (
    <Box sx={containerSx}>
      <Grid container spacing={6} sx={gridContainerSx}>
        {/* Column 1: About me + Education/Experience */}
        <Grid item xs={12} md={hasVideo ? 6 : 12} sx={gridItemSx}>
          <Box sx={hasBio ? contentColumnSx : contentColumnNoBioSx}>
            {hasBio && (
              <>
                <Typography
                  variant="text-md-semibold"
                  color={({ backrs }) => backrs.colors['Colors/Text/text-brand-tertiary-alt']}
                  sx={sectionTitleSx}
                >
                  {COACH_PROFILE_STRINGS.aboutMe}
                </Typography>

                <Typography variant="text-md-regular" sx={bioSx}>
                  {coaching.bio}
                </Typography>
              </>
            )}

            <CoachEducationExperienceSection
              education={coaching.education}
              employers={coaching.employers}
            />
          </Box>
        </Grid>

        {/* Column 2: Meet {first_name} video */}
        {hasVideo && (
          <Grid item xs={12} md={6} sx={gridVideoItemSx}>
            <Typography
              variant="text-sm-semibold"
              color={({ backrs }) => backrs.colors['Colors/Text/text-brand-tertiary-alt']}
            >
              {COACH_PROFILE_STRINGS.meetCoach(
                personName({ first_name: coaching.first_name, last_name: null }),
              )}
            </Typography>

            <VideoUpload mode="view" videoUrl={purposeVideoUrl} />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  marginX: {
    xs: backrs.spacing['spacing-xl'],
    sm: backrs.spacing['spacing-4xl'],
  },
  paddingTop: backrs.spacing['spacing-3xl'],
});

const gridContainerSx: SxProps<Theme> = ({ backrs }) => ({
  marginY: {
    xs: backrs.spacing['spacing-none'],
    md: backrs.spacing['spacing-3xl'],
  },
});

const gridItemSx: SxProps<Theme> = ({ backrs }) => ({
  paddingTop: { md: `${backrs.spacing['spacing-none']} !important` },
});

const gridVideoItemSx: SxProps<Theme> = ({ backrs }) => ({
  paddingTop: { md: `${backrs.spacing['spacing-none']} !important` },
  display: 'flex',
  flexDirection: 'column',
  rowGap: backrs.spacing['spacing-md'],
});

const contentColumnSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: '75% 25%',
  gap: backrs.spacing['spacing-xl'],
});

const contentColumnNoBioSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
});

const sectionTitleSx: SxProps<Theme> = {
  gridColumn: '1 / -1',
  display: 'block',
  marginBottom: 0,
};

const bioSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
  whiteSpace: 'pre-line',
});
